import React, { useEffect, useState } from 'react'
import {
  DashboardPositionFormContentCard,
  DashboardPositionFormSection,
  DashboardPositionFormSectionTitle,
} from 'containers/dashboard/positions/styles'
import { DashboardPositionStore } from 'stores/dashboard/dashboard-position'
import { observer } from 'mobx-react'
import {
  CheckboxChip,
  Chip,
  ChipSize,
  ChipWrapper,
  RadioChip,
} from 'components/common/chip'
import { Column, Row } from 'components/common/layout'
import { PositionFormSubInfoTitleText } from 'components/dashboard/positions/styles'
import { OptionItem, Select, SelectSize } from 'components/common/select'
import { CareerTypeId } from 'types/common'
import { BaseText } from 'components/common/text'
import {
  DASHBOARD_POSITION_FORM_INTERN_JOB_INTEREST_LEVELS,
  DEV_PARENT_POSITION_ID,
  FILTER_EXPERIENCED_JOB_INTEREST_LEVELS,
} from 'infra/constants'
import { DashboardPositionFormSlider } from './position-form-slider'
import { putRequestDashboardStartup } from 'apis/request'
import { mutate } from 'swr'
import { useBaseInfo, useDashboardStartupDetail } from 'apis/hooks'

export const DashboardPositionFormCareer: React.FC<{
  store: DashboardPositionStore
}> = observer(({ store }) => {
  const { data: baseInfo } = useBaseInfo()
  const { data: startup } = useDashboardStartupDetail()
  const [hasCto, setHasCto] = useState<boolean | null>(null)

  useEffect(() => {
    if (!startup || hasCto !== null) return
    setHasCto(startup.hasCto)
  }, [startup, hasCto])

  return (
    <DashboardPositionFormSection>
      <DashboardPositionFormSectionTitle>
        경력
      </DashboardPositionFormSectionTitle>
      <DashboardPositionFormContentCard>
        <ChipWrapper>
          {baseInfo?.careerTypes.map((item) => (
            <Chip
              size={ChipSize.SMALL}
              key={item.id}
              isSelected={store.position.careerType?.id === item.id}
              onClick={() => {
                store.setCareerType(item)
                store.setExperienceRange({ min: 0, max: 10 })
                store.setJobInterestLevel(null)
                store.setInternPeriod(null)
                store.onDataChanged()
              }}
            >
              {item.name}
            </Chip>
          ))}
        </ChipWrapper>
        {store.position.careerType?.id === CareerTypeId.INTERN && (
          <>
            <Column style={{ gap: 8 }}>
              <PositionFormSubInfoTitleText>
                인턴 기간을 선택해주세요
              </PositionFormSubInfoTitleText>
              <Row style={{ gap: 8 }}>
                <Select
                  size={SelectSize.SMALL}
                  width={80}
                  value={
                    store.position.internPeriod
                      ? store.position.internPeriod.toString()
                      : null
                  }
                  placeholder='선택'
                >
                  {[...Array(12)].map((_, index) => {
                    const period = index + 1
                    return (
                      <OptionItem
                        key={period}
                        isSelected={store.position.internPeriod === period}
                        onClick={() => {
                          store.setInternPeriod(period)
                          store.onDataChanged()
                        }}
                      >
                        {period}
                      </OptionItem>
                    )
                  })}
                </Select>
                <BaseText>개월</BaseText>
              </Row>
            </Column>
            <Column style={{ gap: 8 }}>
              <PositionFormSubInfoTitleText>
                정규직 전환을 고려하시나요?
              </PositionFormSubInfoTitleText>
              {DASHBOARD_POSITION_FORM_INTERN_JOB_INTEREST_LEVELS.map(
                (item) => (
                  <RadioChip
                    key={item.id}
                    size={ChipSize.SMALL}
                    isSelected={store.position.jobInterestLevel === item.id}
                    onClick={() => {
                      if (store.position.jobInterestLevel === item.id) {
                        store.setJobInterestLevel(null)
                      } else {
                        store.setJobInterestLevel(item.id)
                      }
                      store.onDataChanged()
                    }}
                  >
                    {item.name}
                  </RadioChip>
                ),
              )}
            </Column>
          </>
        )}
        {store.position.careerType?.id === CareerTypeId.SENIOR && (
          <>
            <DashboardPositionFormSlider
              step={1}
              min={0}
              max={10}
              description='찾으시는 연차가 있나요?'
              value={[
                store.position.experienceRange?.min || 0,
                store.position.experienceRange?.max || 10,
              ]}
              unit='년'
              onChange={(v) => {
                const _v = v as number[]
                store.setExperienceRange({ min: _v[0], max: _v[1] })
                store.onDataChanged()
              }}
            />
            <Column style={{ gap: 8 }}>
              <PositionFormSubInfoTitleText>
                리더를 찾으시나요? (선택)
              </PositionFormSubInfoTitleText>
              {FILTER_EXPERIENCED_JOB_INTEREST_LEVELS.map((item) => (
                <RadioChip
                  key={item.id}
                  size={ChipSize.SMALL}
                  isSelected={store.position.jobInterestLevel === item.id}
                  onClick={() => {
                    if (store.position.jobInterestLevel === item.id) {
                      store.setJobInterestLevel(null)
                    } else {
                      store.setJobInterestLevel(item.id)
                    }
                    store.onDataChanged()
                  }}
                >
                  {item.name}
                </RadioChip>
              ))}
            </Column>
          </>
        )}
        {store.position.careerType && (
          <Column style={{ gap: 8 }}>
            <PositionFormSubInfoTitleText>
              <Column style={{ gap: 8 }}>
                <PositionFormSubInfoTitleText>
                  업무 관련 도움을 주실 분이 계신가요? (선택)
                </PositionFormSubInfoTitleText>
                <CheckboxChip
                  size={ChipSize.SMALL}
                  isSelected={store.position.hasSupervisor}
                  onClick={() => {
                    store.setHasSupervisor(!store.position.hasSupervisor)
                    store.onDataChanged()
                  }}
                >
                  사수 있음
                </CheckboxChip>
                {store.position.positionTypes
                  .map((item) => item.parentId)
                  .includes(DEV_PARENT_POSITION_ID) && (
                  <CheckboxChip
                    size={ChipSize.SMALL}
                    isSelected={!!hasCto}
                    onClick={async () => {
                      setHasCto(!hasCto)
                      const formData = new FormData()
                      formData.append('hasCto', String(!hasCto) ?? '')
                      await putRequestDashboardStartup(formData)
                      mutate('/dashboard/startup')
                    }}
                  >
                    CTO 있음
                  </CheckboxChip>
                )}
              </Column>
            </PositionFormSubInfoTitleText>
          </Column>
        )}
      </DashboardPositionFormContentCard>
    </DashboardPositionFormSection>
  )
})
